import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiDownload, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { formatINR } from '../../utils/currency';
import { API_ENDPOINTS } from '../../utils/api';
import Swal from 'sweetalert2';

const emptyForm = {
  type: 'income',
  category: '',
  amount: '',
  description: '',
  date: new Date().toISOString().split('T')[0],
  paymentMethod: 'cash'
};

const incomeCategories = ['Sales Revenue', 'Service Revenue', 'Other Income'];
const expenseCategories = ['Raw Materials', 'Salaries', 'Utilities', 'Rent', 'Marketing', 'Other Expenses'];

const IncomeExpense = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ totalIncome: 0, totalExpense: 0, netIncome: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.getIncomeExpense(selectedPeriod), {
        headers: getAuthHeaders()
      });
      setTransactions(response.data?.transactions || []);
      setSummary(response.data?.summary || { totalIncome: 0, totalExpense: 0, netIncome: 0 });
    } catch (error) {
      console.error('Error fetching transactions:', error);
      Swal.fire('Error', 'Failed to load transactions', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        type: formData.type,
        category: formData.category,
        amount: Number(formData.amount) || 0,
        description: formData.description,
        date: formData.date,
        paymentMethod: formData.paymentMethod
      };

      if (selectedTransaction) {
        await axios.put(API_ENDPOINTS.updateIncomeExpense(selectedTransaction.id), payload, {
          headers: getAuthHeaders()
        });
      } else {
        await axios.post(API_ENDPOINTS.createIncomeExpense, payload, {
          headers: getAuthHeaders()
        });
      }

      Swal.fire('Success', selectedTransaction ? 'Transaction updated successfully' : 'Transaction created successfully', 'success');
      setShowModal(false);
      setSelectedTransaction(null);
      setFormData(emptyForm);
      fetchTransactions();
    } catch (error) {
      console.error('Error saving transaction:', error);
      Swal.fire('Error', error.response?.data?.error || 'Failed to save transaction', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(API_ENDPOINTS.deleteIncomeExpense(id), {
          headers: getAuthHeaders()
        });
        Swal.fire('Deleted', 'Transaction has been deleted', 'success');
        fetchTransactions();
      } catch (error) {
        console.error('Error deleting transaction:', error);
        Swal.fire('Error', error.response?.data?.error || 'Failed to delete transaction', 'error');
      }
    }
  };

  const handleExport = () => {
    if (transactions.length === 0) {
      Swal.fire('No data', 'No transactions to export for this period', 'info');
      return;
    }

    const headers = ['Date', 'Type', 'Category', 'Description', 'Amount', 'Payment Method'];
    const rows = transactions.map((t) => [
      t.date,
      t.type,
      t.category,
      `"${String(t.description).replace(/"/g, '""')}"`,
      t.amount,
      t.paymentMethod
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
      '',
      `Total Income,${summary.totalIncome}`,
      `Total Expense,${summary.totalExpense}`,
      `Net Income,${summary.netIncome}`
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `income-expense-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const { totalIncome, totalExpense, netIncome } = summary;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiDollarSign className="text-blue-600" />
            Income & Expense Management
          </h1>
          <p className="text-gray-600 mt-1">Track company income and expenses</p>
        </div>
        <div className="flex gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
          <button
            type="button"
            onClick={handleExport}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiDownload /> Export
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Total Income</h3>
            <FiTrendingUp className="text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-600">{formatINR(totalIncome)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Total Expense</h3>
            <FiTrendingDown className="text-red-600" />
          </div>
          <p className="text-3xl font-bold text-red-600">{formatINR(totalExpense)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Net Income</h3>
            <FiDollarSign className={netIncome >= 0 ? 'text-green-600' : 'text-red-600'} />
          </div>
          <p className={`text-3xl font-bold ${netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatINR(netIncome)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Transactions</h2>
          <button
            onClick={() => {
              setSelectedTransaction(null);
              setFormData(emptyForm);
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiPlus /> New Transaction
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Payment Method</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12 text-gray-500">
                      No transactions found for this period
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr key={transaction.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{transaction.date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          transaction.type === 'income'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {transaction.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{transaction.category}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{transaction.description}</td>
                      <td className={`px-4 py-3 text-sm font-semibold ${
                        transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'income' ? '+' : '-'}{formatINR(parseFloat(transaction.amount))}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 capitalize">
                        {transaction.paymentMethod.replace('_', ' ')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedTransaction(transaction);
                              setFormData({
                                type: transaction.type,
                                category: transaction.category,
                                amount: transaction.amount,
                                description: transaction.description,
                                date: transaction.date,
                                paymentMethod: transaction.paymentMethod
                              });
                              setShowModal(true);
                            }}
                            className="text-green-600 hover:text-green-800"
                            title="Edit"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(transaction.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {selectedTransaction ? 'Edit Transaction' : 'New Transaction'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value, category: '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select category</option>
                    {(formData.type === 'income' ? incomeCategories : expenseCategories).map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="credit_card">Credit Card</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedTransaction(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {selectedTransaction ? 'Update' : 'Create'} Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeExpense;
